import React, { useEffect, useState } from "react";
import Checkbox from "@/Components/Form/Checkbox";
import AuthServices from "@/Layouts/AuthServices";
import { Head, router } from "@inertiajs/react";
import Link from "@/Components/Link";
import Form, { SubmissionButtons } from "@/Components/Form/Form";
import TextInput from "@/Components/Form/TextInput";
import * as yup from "yup";
import Alert from "@/Components/Alert";
import WebAuthnHandler from "./Helpers/WebAuthnHandler";
import { Transition } from "@headlessui/react";
import SSOHandler from "@/Pages/Auth/Helpers/SSOHandler";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import Grid from "@/Components/Grid";

const Login = ({ status, canResetPassword, tenant }) => {
    const [showPasswordField, setShowPasswordField] = useState(true);
    const [showWebauthn, setShowWebauthn] = useState(false);
    const [canUsePlatformAuthenticator, setCanUsePlatformAuthenticator] =
        useState(false);
    const [ssoUrl, setSsoUrl] = useState(null);

    useEffect(() => {
        (async () => {
            if (await browserSupportsWebAuthn()) {
                setShowWebauthn(true);
                setCanUsePlatformAuthenticator(true);
            }
        })();
    }, []);

    const onSubmit = (values) => {
        if (ssoUrl) {
            window.location.href = ssoUrl;
        } else {
            router.post(route("login"), values, {
                // onSuccess: (arg) => console.log(arg),
            });
        }
    };

    // If SSO, hide password and webauthn
    useEffect(() => {
        setShowPasswordField(ssoUrl === null);
        setShowWebauthn(ssoUrl === null && canUsePlatformAuthenticator);
    }, [ssoUrl]);

    const validationSchema = {
        email: yup
            .string()
            .required("An email address is required")
            .email("Your email address must be valid"),
    };

    if (!ssoUrl) {
        validationSchema.password = yup
            .string()
            .required("A password is required");
        validationSchema.remember = yup
            .boolean()
            .oneOf([false, true], "Remember me must be ticked or not ticked");
    }

    return (
        <AuthServices title="Sign in to your account">
            <Head title="Log in">
                <meta
                    name="description"
                    content={`Sign in to your ${tenant.name} account.`}
                />
                <meta
                    property="og:title"
                    content={`Log in to ${tenant.name}`}
                />

                <meta
                    property="og:description"
                    content={`Sign in to your ${tenant.name} account.`}
                />
            </Head>

            {status && (
                <Alert title="Success" className="mb-4">
                    {status}
                </Alert>
            )}

            <Form
                initialValues={{
                    email: "",
                    password: "",
                    remember: true,
                }}
                validationSchema={yup.object().shape(validationSchema)}
                onSubmit={onSubmit}
                submitTitle="Sign in"
                submitClass="w-full"
                hideClear
                hideDefaultButtons
            >
                <WebAuthnHandler show={showWebauthn} />

                <Grid>
                    <TextInput
                        name="email"
                        type="email"
                        label="Email"
                        autoComplete="username webauthn"
                        inputMode="email"
                    />

                    <Transition
                        show={showPasswordField}
                        enter="transition duration-500"
                        enterFrom="opacity-0 scale-0 height-0"
                        enterTo="opacity-100 scale-100 height-100"
                        leave="transition duration-150"
                        leaveFrom="opacity-100 scale-100 height-100"
                        leaveTo="opacity-0 scale-0 height-0"
                    >
                        <div>
                            <Grid>
                                <TextInput
                                    name="password"
                                    type="password"
                                    label="Password"
                                    autoComplete="current-password"
                                />

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <Checkbox
                                            name="remember"
                                            label="Remember me"
                                        />
                                    </div>

                                    {canResetPassword && (
                                        <div className="mb-3 text-sm">
                                            <Link
                                                href={route("password.request")}
                                            >
                                                Forgot your password?
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </Grid>
                        </div>
                    </Transition>

                    <SubmissionButtons />
                    <SSOHandler setSsoUrl={setSsoUrl} />
                </Grid>
            </Form>
        </AuthServices>
    );
};

export default Login;
